import { store } from '../store/configureStore'

import { ROOT_NODE_ID } from '../store/migrations/data-structure-conversion'

import {
    toggleNode,
    setParent,
    postNode,
    patchNode,
    setPreviousSibling,
    setNextSibling,
    selectBestChildIdFor
} from '../controllers/treeEngine'
import {
    startTextEdit,
    endTextEdit,
    endTextEditNoSave,
    deleteCursorNode,
    moveCursorTo,
    moveCursorToBestChildOrSelf,
    selectCursorNode
} from '../store/cursor'
import { generateUID } from '../utils/generateUID'

import { scrollAfterArrowKey } from './autoScroll'

const { dispatch, getState } = store

export const initKeyboardListener = () => {
    document.addEventListener('keydown', keyDownLogic)
    document.addEventListener('keyup', keyUpLogic)
    window.addEventListener('focus', resetPressedKeys)
}

let pressedKeys = {}

const keysToNodeTypes = {
    KeyA: 'checkbox',
    AltLeft: 'checkbox',
    AltRight: 'checkbox',
    KeyE: 'text',
    KeyT: 'text',
    KeyD: 'done',
    KeyF: 'failed',
    KeyW: 'waiting',
    KeyQ: 'idea',
    KeyC: 'cancelled'
}

const resetPressedKeys = () => {
    pressedKeys = {}
}

const up = (node) => {
    if (node.previousSiblingId) {
        // cursorNode.nextSibling = cursorNode.previousSibling
        setNextSibling(node.id, node.previousSiblingId)
    }
}

const down = (node) => {
    if (node.nextSiblingId) {
        // cursorNode.previousSibling = cursorNode.nextSibling
        setPreviousSibling(node.id, node.nextSiblingId)
    }
}

const indent = (node) => {
    if (node.previousSiblingId) {
        // cursorNode.parent = cursorNode.previousSibling
        setParent(node.id, node.previousSiblingId)
    }
}

const deindent = (node) => {
    if (node.parentId !== ROOT_NODE_ID) {
        // cursorNode.previousSibling = cursorNode.parent
        setPreviousSibling(node.id, node.parentId)
    }
}

const createNode = (node) => {
    if (!node) node = {}
    if (!node.id) node.id = generateUID()
    postNode(node)
    dispatch(moveCursorTo(node.id))
    dispatch(startTextEdit())
    return node.id
}

const keyDownLogic = (event) => {
    const key = event.code
    pressedKeys[key] = true

    const cursorNode = selectCursorNode(getState()) || {}
    const editingText = getState().cursor.editingText
    const bestChildIdForCursor = cursorNode && selectBestChildIdFor(cursorNode.id)

    const pressedTypeKeys = Object.keys(pressedKeys)
        .filter((x) => pressedKeys[x] === true)
        .filter((x) => x in keysToNodeTypes)

    scrollAfterArrowKey()

    if (editingText) {
        if (key === 'Enter' || key === 'NumpadEnter') {
            dispatch(endTextEdit())
            if (cursorNode.text) {
                const type = cursorNode.type
                const node = {}
                if (type === 'text') {
                    node.type = type
                }
                node.previousSiblingId = cursorNode.id
                createNode(node)
            }
            resetPressedKeys()
        } else if (key === 'Escape') {
            dispatch(endTextEditNoSave())
        } else if (key === 'Tab') {
            if (event.shiftKey) {
                deindent(cursorNode)
            } else {
                indent(cursorNode)
            }
        } else if (event.metaKey && event.altKey) {
            if (key === 'ArrowDown') down(cursorNode)
            else if (key === 'ArrowUp') up(cursorNode)
            else if (key === 'ArrowRight') indent(cursorNode)
            else if (key === 'ArrowLeft') deindent(cursorNode)
            else return
        } else {
            return
        }
        event.preventDefault()
    } else if ((event.metaKey && event.altKey) || event.ctrlKey) {
        if (key === 'ArrowDown') down(cursorNode)
        else if (key === 'ArrowUp') up(cursorNode)
        else if (key === 'ArrowRight') indent(cursorNode)
        else if (key === 'ArrowLeft') deindent(cursorNode)
        else return
        event.preventDefault()
        // Add node
    } else if (pressedTypeKeys.length === 1) {
        const type = keysToNodeTypes[pressedTypeKeys[0]]
        const node = {}
        node.type = type
        if (key === 'ArrowDown' || key === 'Enter' || key === 'NumpadEnter') {
            node.previousSiblingId = cursorNode.id
            createNode(node)
        } else if (key === 'ArrowUp') {
            node.nextSiblingId = cursorNode.id
            createNode(node)
        } else if (key === 'ArrowRight') {
            node.parentId = cursorNode.id
            if (cursorNode.type === 'text') {
                node.type = cursorNode.type
            }
            createNode(node)
        } else if (key === 'ArrowLeft') {
            const oldCursorNodeId = cursorNode.id
            node.nextSiblingId = cursorNode.id
            const id = createNode(node)
            setParent(oldCursorNodeId, id)
        } else {
            return
        }
        event.preventDefault()
    } else if (pressedTypeKeys.length === 0) {
        if (key === 'Backspace' || key === 'Delete') {
            if (cursorNode.childrenIds.length > 0) {
                alert("Can't remove a task with subtasks.")
            } else {
                dispatch(deleteCursorNode())
            }
        } else if (key === 'Enter' || key === 'NumpadEnter') {
            if (cursorNode.id) {
                dispatch(startTextEdit())
            } else {
                createNode()
            }
        } else if (key === 'ArrowDown') {
            dispatch(moveCursorTo(cursorNode.nextSiblingId))
        } else if (key === 'ArrowUp') {
            dispatch(moveCursorTo(cursorNode.previousSiblingId))
        } else if (key === 'ArrowRight') {
            const bestChild = bestChildIdForCursor
            if (bestChild) {
                dispatch(moveCursorToBestChildOrSelf(cursorNode.id))
            } else {
                // Disabled until feature/return-on-opposite-direction
                // createNode({ parentId: cursorNode.id })
            }
        } else if (key === 'ArrowLeft') {
            dispatch(moveCursorTo(cursorNode.parentId))
        } else if (key === 'Space') {
            toggleNode(cursorNode.id)
        } else if (key === 'Tab') {
            if (event.shiftKey) {
                deindent(cursorNode)
            } else {
                indent(cursorNode)
            }
        } else {
            return
        }
        event.preventDefault()
    }
    scrollAfterArrowKey()
}

const keyUpLogic = (event) => {
    const key = event.code
    if (!pressedKeys[key]) {
        return
    }
    pressedKeys[key] = false

    const cursorNode = selectCursorNode(getState()) || {}

    if (event.target === document.body) {
        if (key in keysToNodeTypes && key !== 'AltLeft' && key !== 'AltRight') {
            const type = keysToNodeTypes[key]
            if (cursorNode) {
                patchNode(cursorNode.id, {
                    type
                })
            }
        }
    }
}
